import React, { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { Provider, useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { Global404 } from '@saviynt/common';

import config from '../../config/obNextGenConfig';
import { useTypedSelector } from '../../hooks/use-typedselector';
import { store } from '../../redux/obNextGenStore';
import { type RootState } from '../../services/serviceTypes';
import messagesClient from '../../translations';
import {
  hideHostAppElements,
  hideHostAppElementsCleanup,
  removeDynamicBodyClasses,
} from '../../utils/hideHostElements';
import Copilot from '../Copilot/Copilot';
import Discovery from '../Discovery/discovery';
import GraphsPage from '../GraphsPOC/GraphsPage';
import IntegrationsDashboard from '../IntegrationsDashboard/IntegrationsDashboard';
import ManageCMDB from '../ManageCMDB/ManageCMDB';

import './PagesRoute.css';

type Locale = keyof typeof messagesClient;

/**
 * Inner component that uses Redux selectors - must be inside Provider
 */
function PagesRouteInner() {
  useEffect(() => {
    /* hide host app elements not needed for Gen2 UI */
    hideHostAppElements();
    removeDynamicBodyClasses();

    return () => {
      hideHostAppElementsCleanup();
    };
  }, []);

  // Get the ispm enable service from host app store values
  const INTEGRATION_SERVICES_ENABLED = useSelector<RootState, boolean>(
    (state) => state.user.domainInfo?.INTEGRATION_SERVICE_ENABLED ?? false
  );
  const CUA_MT_ENABLED = useSelector<RootState, boolean>(
    (state) => state.user.domainInfo?.CUA_MT_ENABLED ?? false
  );
  const APPLICATION_LOCALE = useTypedSelector(
    (state) => state.user.userInfo?.userSavRoleMap?.locale ?? 'en'
  );

  const [locale] = useState<Locale>(navigator.language);

  /**
   * If INTEGRATIONS services is not enabled, return 404
   */
  if (!INTEGRATION_SERVICES_ENABLED) {
    return (
      <div className='Ispm-Global404'>
        <Global404 />
      </div>
    );
  }

  return (
    <IntlProvider locale={locale} messages={messagesClient[APPLICATION_LOCALE]}>
      <Switch>
        <Route
          exact
          path={config.URL_OB_NEXT_GEN_DISCOVERY}
          render={() => <Discovery />}
        />
        <Route
          exact
          path={config.URL_OB_NEXT_GEN}
          render={() => <IntegrationsDashboard />}
        />
        <Route
          exact
          path={config.URL_OB_NEXT_GEN_DISCOVERY_CONFIG}
          render={() => <ManageCMDB />}
        />
        <Route
          exact
          path={config.URL_OB_NEXT_GEN_GRAPHS_POC}
          render={() => <GraphsPage />}
        />
        {CUA_MT_ENABLED && (
          <Route
            exact
            path={config.URL_INTEGRATIONS_COPILOT}
            render={() => <Copilot />}
          />
        )}
        <Route
          exact
          path='*'
          render={() => (
            <div className='Ispm-Global404'>
              <Global404 />
            </div>
          )}
        />
      </Switch>
    </IntlProvider>
  );
}

/**
 * Page Route for Integrations
 */
function PagesRoute() {
  return (
    <React.StrictMode>
      <Provider store={store}>
        <PagesRouteInner />
      </Provider>
    </React.StrictMode>
  );
}

export default PagesRoute;
